import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { ToastProvider } from '@/hooks/useToast';
import { Shell } from '@/components/layout/Shell';
import Login from '@/pages/auth/Login';
import Dashboard from '@/pages/Dashboard';
import PiList from '@/pages/pi/PiList';
import PiCreate from '@/pages/pi/PiCreate';
import PiDetail from '@/pages/pi/PiDetail';
import Customers from '@/pages/admin/Customers';
import Products from '@/pages/admin/Products';
import MachineConfigs from '@/pages/admin/MachineConfigs';
import PriceConfig from '@/pages/admin/PriceConfig';
import PaymentPresets from '@/pages/admin/PaymentPresets';
import WarrantyPresets from '@/pages/admin/WarrantyPresets';
import Settings from '@/pages/admin/Settings';
import Users from '@/pages/admin/Users';
import Account from '@/pages/account/Account';

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
});

function Protected() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function AdminOnly() {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<Protected />}>
                <Route element={<Shell />}>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/pis" element={<PiList />} />
                  <Route path="/pis/new" element={<PiCreate />} />
                  <Route path="/pis/:id" element={<PiDetail />} />
                  <Route path="/pis/:id/edit" element={<PiCreate />} />
                  <Route path="/customers" element={<Customers />} />
                  <Route path="/account" element={<Account />} />
                  {/* Reference/catalog tabs: viewable by any authenticated user
                      (manager or admin); write actions inside each page are
                      gated to admin only. Users stays fully admin-only. */}
                  <Route path="/admin/products" element={<Products />} />
                  <Route path="/admin/machine-configs" element={<MachineConfigs />} />
                  <Route path="/admin/payment-presets" element={<PaymentPresets />} />
                  <Route path="/admin/warranty-presets" element={<WarrantyPresets />} />
                  <Route path="/admin/settings" element={<Settings />} />
                  <Route element={<AdminOnly />}>
                    <Route path="/admin/price-config" element={<PriceConfig />} />
                    <Route path="/admin/users" element={<Users />} />
                  </Route>
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
